/**
 * Which band each SSID interface is on.
 *
 * This is the OPERATOR'S standard layout, not a fact any device reports. The
 * chassis names SSID interfaces `wifi_0/1` … `wifi_0/8` — `show gpon remote-onu
 * ssid info` prints the interface, the name, the auth and the hiding, and says
 * nothing at all about frequency — so there is no reading to derive this from.
 * It is a provisioning convention, stated by the operator (2026-08-25) as:
 *
 *   wifi 1-4  → 2.4 GHz
 *   wifi 5-8  → 5 GHz
 *
 * It lives in one named constant, in the contract layer, for two reasons. It is
 * the single assumption here that a different deployment could legitimately
 * contradict, so a fleet cabled to another convention changes this and nothing
 * else — scattering `index <= 4` checks through resolvers and forms is what
 * would turn that into a hunt. And both sides need it: the API resolves a
 * preset's bands to ports, and the operator's own form labels and validates
 * them. Like `subnet.ts`, this is a fact about the network rather than about a
 * screen.
 *
 * TR-069 does NOT use this. There the CPE reports its own band per WLAN
 * instance (`OperatingFrequencyBand`, the referenced radio, the channel), and a
 * reading from the device always beats a convention about cabling.
 */
export const SSID_BAND_PORTS = {
  '2.4G': [1, 2, 3, 4],
  '5G': [5, 6, 7, 8],
} as const satisfies Record<string, readonly number[]>;

export type SsidBand = keyof typeof SSID_BAND_PORTS;

export const SSID_BANDS: readonly SsidBand[] = ['2.4G', '5G'];

/**
 * The primary network on a band — `wifi_0/1` at 2.4 GHz, `wifi_0/5` at 5 GHz.
 *
 * Slot 0 because every ONU in this fleet reports one; the index after the
 * slash is what carries the meaning.
 */
export function primarySsidPort(band: SsidBand): string {
  return `wifi_0/${SSID_BAND_PORTS[band][0]}`;
}

/**
 * The band an SSID interface is on, or null when it falls outside the layout.
 *
 * Null is "this platform does not know", never a guessed band. An SSID renamed
 * or re-keyed on the wrong radio is a subscriber's network moved without
 * anybody having asked for it.
 */
export function bandForSsidPort(port: string): SsidBand | null {
  const match = /^wifi_\d{1,2}\/(\d{1,2})$/.exec(port);
  if (!match) return null;
  const index = Number(match[1]);
  for (const band of SSID_BANDS) {
    if ((SSID_BAND_PORTS[band] as readonly number[]).includes(index)) return band;
  }
  return null;
}

/**
 * "Whichever one is free" — an interface choice that is resolved per ONU
 * instead of being named.
 *
 * A public network goes ALONGSIDE the subscriber's own, and which slot that is
 * differs by model in a way no operator should have to hold in their head.
 * Measured across all 262 informing CPEs on this fleet (2026-09-24): a ZTE
 * F670L reports all eight instances whether used or not, so 37 units carry a
 * dormant 2.4 GHz slot and 37 a dormant 5 GHz one, still on the factory names
 * `SSID2`…`SSID8`; a Huawei HG8546M reports only the SSIDs that EXIST, so 176
 * of them have no spare at all and one has to be created. Asking for "WiFi 2"
 * is therefore right on one vendor and a refusal on the other.
 *
 * So the ANSWER is per ONU: enable the dormant slot where there is one —
 * keeping the hardware's own numbering — and create one where there is not.
 *
 * Never the band's PRIMARY. That is the subscriber's own network, and a
 * primary that is switched off is a real state rather than a free slot: 20
 * bridge-mode hotspot ONUs on this fleet run their public network on exactly
 * that, disabled.
 */
export const NEXT_FREE_SSID_PORT = 'next-free';

/**
 * Is this WLAN the subscriber's own network on its band — the one no delete
 * here ever takes?
 *
 * WiFi 1 and WiFi 5 whatever a CPE says about them: the fleet's primaries by
 * layout, and the operator's own exception when the 2026-09-25 cleanup was
 * scoped ("except on SSID1/SSID5"). And the lowest instance on any band the
 * CPE states, for a model that numbers its radios differently. One rule, so
 * the ONU page's Delete button and the API that refuses it cannot disagree.
 */
export function isPrimaryWlan(
  wlan: { instance: string; band: string },
  onDevice: ReadonlyArray<{ instance: string; band: string }>,
): boolean {
  const n = Number(wlan.instance);
  if (n === SSID_BAND_PORTS['2.4G'][0] || n === SSID_BAND_PORTS['5G'][0]) return true;
  if (wlan.band !== '2.4G' && wlan.band !== '5G') return false;
  const lowest = onDevice
    .filter((w) => w.band === wlan.band)
    .map((w) => Number(w.instance))
    .sort((a, b) => a - b)[0];
  return lowest === n;
}

/**
 * A unit whose subscriber service is a bridge: no routed subscriber VLAN,
 * at least one bridged. Management and VoIP VLANs do not count. The same
 * reading `defaultPortMode` makes for the sockets.
 */
export function isBridgedUnit(vlans: ReadonlyArray<{ vlanId: number; mode: string; purpose: string }>): boolean {
  const subscriber = vlans.filter((v) => v.purpose !== 'management' && v.purpose !== 'voip');
  return subscriber.length > 0 && subscriber.every((v) => v.mode === 'bridge');
}

/**
 * May this network be deleted? The band's own network (`isPrimaryWlan`) is
 * kept on a ROUTED unit: there it is the subscriber's LAN WiFi, and the
 * 2026-09-25 exception ("except on SSID1/SSID5") was written for the bulk
 * hotspot setup on exactly those units. A BRIDGED unit is a switch with no
 * LAN of its own, so every network on it is the operator's to delete
 * (operator, 2026-10-16: "bridge is just a switching setup theres no lan").
 */
export function wlanDeletable(
  wlan: { instance: string; band: string },
  onDevice: ReadonlyArray<{ instance: string; band: string }>,
  vlans: ReadonlyArray<{ vlanId: number; mode: string; purpose: string }>,
): boolean {
  return isBridgedUnit(vlans) || !isPrimaryWlan(wlan, onDevice);
}

/** Is this interface choice "whichever is free" rather than a named one? */
export function isNextFreeSsidPort(port: string | null | undefined): boolean {
  return port === NEXT_FREE_SSID_PORT;
}

/** Every SSID interface on a band, in order — for a picker. */
export function ssidPortsForBand(band: SsidBand): string[] {
  return SSID_BAND_PORTS[band].map((index) => `wifi_0/${index}`);
}

// --- Per-model layout ---------------------------------------------------------

/**
 * What ONE ONU MODEL actually exposes, and what its radios can actually serve.
 *
 * `SSID_BAND_PORTS` above is fleet-wide, and that is its limit: it cannot
 * express a model that differs, and models on this fleet DO differ. Same
 * chassis, same command (`show gpon remote-onu ssid info <iface>`), measured
 * 2026-08-25:
 *
 *   Nokia  G-1425G-B   wifi_0/1 … wifi_0/8   8 interfaces   287 units
 *   Huawei HG8546M     wifi_0/1 … wifi_0/4   4 interfaces   2,165 units
 *   Tenda  HG3V1.0     wifi_0/1              1 interface    8 units
 *
 * The interface count is a property of the MODEL — the HG8546M lists four on
 * the C300 and four on the C650 — so it belongs beside the model rather than
 * beside the fleet. Both fields are NULLABLE and null means "nobody has
 * recorded this", which resolves to the fleet convention and today's exact
 * behaviour. A model nobody has written anything down about must not start
 * behaving differently because this type exists.
 */
export interface OnuSsidLayout {
  /** The model this describes, as the hardware library files it. */
  model: string | null;
  /**
   * The `wifi_0/N` indices the chassis lists for this model, ascending. Null
   * when no count has been recorded — NOT an empty list, which would claim the
   * model has no SSID interfaces at all.
   */
  interfaces: readonly number[] | null;
  /** The radios this model is known to have. Null when nobody has recorded. */
  bands: readonly SsidBand[] | null;
  /** Where `bands` came from, so a refusal can cite its evidence. */
  bandsSource: string | null;
}

/** `wifi_0/5` → 5. Null for anything that is not an SSID interface name. */
export function ssidIndexOf(port: string): number | null {
  const match = /^wifi_\d{1,2}\/(\d{1,2})$/.exec(port);
  return match ? Number(match[1]) : null;
}

/** `[1,2,3,4]` → "wifi_0/1–wifi_0/4"; a gapped set is listed in full. */
export function describeSsidInterfaces(interfaces: readonly number[]): string {
  if (interfaces.length === 0) return 'no SSID interfaces';
  const sorted = [...interfaces].sort((a, b) => a - b);
  const contiguous = sorted.every((n, i) => i === 0 || n === sorted[i - 1]! + 1);
  if (sorted.length === 1) return `wifi_0/${sorted[0]}`;
  if (contiguous) return `wifi_0/${sorted[0]}–wifi_0/${sorted[sorted.length - 1]}`;
  return sorted.map((n) => `wifi_0/${n}`).join(', ');
}

/** The interface indices a model of `count` SSID interfaces exposes. */
export function ssidInterfacesForCount(count: number | null | undefined): number[] | null {
  if (count === null || count === undefined || !Number.isFinite(count) || count <= 0) return null;
  return Array.from({ length: Math.trunc(count) }, (_, i) => i + 1);
}

export type SsidPortResolution =
  | { ok: true; port: string }
  /**
   * `reason` is a clause that follows an ONU's serial — "…, and the chassis
   * lists wifi_0/1–wifi_0/4 for that model". The caller supplies the subject
   * and the consequence; this supplies the fact.
   */
  | { ok: false; reason: string };

/**
 * The interface a band's SSID must be written to on THIS ONU — or the reason
 * there isn't one.
 *
 * The rule that matters is what it will NOT do: when a model exposes no
 * interface on the band being addressed, this refuses and names the band. It
 * never falls back to an interface on the other radio. A 5 GHz name quietly
 * written to `wifi_0/1` is a subscriber's 2.4 GHz network renamed and re-keyed
 * by an operator who believes they touched the other one, and every refusal in
 * this area exists to stop precisely that.
 *
 * With no layout recorded this is the fleet convention and nothing else, which
 * is what the platform did before per-model layouts existed.
 */
export function ssidPortForBand(
  band: SsidBand,
  layout: OnuSsidLayout | null | undefined,
  chosen?: string | null,
): SsidPortResolution {
  const model = layout?.model ?? null;
  const forModel = model ? ` for ${model}` : '';

  // A radio the hardware does not have. Nothing on any interface can serve it.
  if (layout?.bands && !layout.bands.includes(band)) {
    const cite = layout.bandsSource ? ` (${layout.bandsSource})` : '';
    return {
      ok: false,
      reason: model
        ? `is a ${model}, which has no ${band} radio${cite}`
        : `is a model with no ${band} radio${cite}`,
    };
  }

  const exposed = layout?.interfaces ?? null;

  if (chosen) {
    const index = ssidIndexOf(chosen);
    if (index !== null && exposed && !exposed.includes(index)) {
      return {
        ok: false,
        reason: `does not expose ${chosen} — the chassis lists ${describeSsidInterfaces(exposed)}${forModel || ' for this model'}`,
      };
    }
    return { ok: true, port: chosen };
  }

  const candidates = (SSID_BAND_PORTS[band] as readonly number[]).filter(
    (index) => !exposed || exposed.includes(index),
  );
  if (candidates.length === 0) {
    return {
      ok: false,
      reason: `exposes ${describeSsidInterfaces(exposed ?? [])}${forModel || ' for this model'}, and none of those is a ${band} interface in this fleet's layout`,
    };
  }
  return { ok: true, port: `wifi_0/${candidates[0]}` };
}

/**
 * The band to PRINT beside an interface, which is a weaker claim than the one
 * above and must be treated as one.
 *
 * A label saying "WiFi 5 (5G)" on a model with no 5 GHz radio is the same lie
 * as writing to it, in smaller type. So the convention's answer is only
 * repeated where the model's known radios can back it; otherwise the label
 * says less rather than something unsupported. Null means "say nothing".
 */
export function bandLabelForSsidPort(
  port: string,
  layout?: OnuSsidLayout | null,
): SsidBand | null {
  const band = bandForSsidPort(port);
  if (!band) return null;
  if (layout?.bands && !layout.bands.includes(band)) return null;
  return band;
}
